import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { PersonAdd as PersonAddIcon } from '@mui/icons-material'
import type { Contact } from '@otw/models/leads/contacts'
import React, { useEffect, useMemo, useState } from 'react'

interface SelectContactForLeadDialogProps {
  open: boolean
  /** Contacts associated with the company. Must be passed already filtered. */
  contacts: Contact[]
  /** Display name of the company for context in the title. */
  companyName: string
  onClose: () => void
  onConfirm: (contact: Contact) => void
  /** Dialog title. Defaults to lead copy. */
  title?: string
  /** When true (default), only contacts with a portal client account are listed. */
  requirePortalAccount?: boolean
  /** Shown when there are no selectable contacts. */
  emptyMessage?: string
  /** Autocomplete helper when contacts are available. */
  contactHelperText?: string
}

/**
 * Forces the user to pick which member contact an action belongs to before
 * continuing (new lead, task, etc.).
 */
const SelectContactForLeadDialog: React.FC<SelectContactForLeadDialogProps> = ({
  open,
  contacts,
  companyName,
  onClose,
  onConfirm,
  title = 'Who is this lead for?',
  requirePortalAccount = true,
  emptyMessage = 'No contacts at this company have a portal client account yet. Open the contact and create an account first before logging a lead.',
  contactHelperText = 'Only contacts with a portal client account are listed.',
}) => {
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const options = useMemo(() => {
    if (!requirePortalAccount) return contacts
    return contacts.filter((c) => Boolean(c.userId?.trim()))
  }, [contacts, requirePortalAccount])

  const [value, setValue] = useState<Contact | null>(null)

  useEffect(() => {
    if (!open) return
    setValue(options.length === 1 ? options[0] : null)
  }, [open, options])

  const canSubmit = Boolean(value)

  const handleConfirm = () => {
    if (!value) return
    onConfirm(value)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: alpha(accent, 0.15),
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonAddIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              At {companyName || 'this company'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {options.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {emptyMessage}
            </Alert>
          ) : null}
          <Autocomplete
            options={options}
            value={value}
            onChange={(_, next) => setValue(next)}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            disabled={options.length === 0}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name || 'Unnamed contact'}
                  </Typography>
                  {option.email || option.phone ? (
                    <Typography variant="caption" color="text.secondary">
                      {[option.email, option.phone].filter(Boolean).join(' • ')}
                    </Typography>
                  ) : null}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Contact"
                required
                error={!canSubmit && options.length > 0}
                helperText={
                  options.length === 0 ? 'No contacts available.' : contactHelperText
                }
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canSubmit}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SelectContactForLeadDialog
